import { useMemo, useRef, useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Broadcast,
  Radio,
  Trophy,
  Television,
  Newspaper,
  Globe,
  Leaf,
  MusicNotes,
  FilmSlate,
  Eye,
} from "@phosphor-icons/react";
import { Header } from "@/components/Header";
import { OnAirHero } from "@/components/OnAirHero";
import { fetchLive, formatViews } from "@/lib/api";
import { onImgError } from "@/lib/fallback";

const CATEGORY_ORDER = ["Sports", "News", "Weather & Earth", "Nature", "Music", "Entertainment"];

const CATEGORY_META = {
  News: { icon: Newspaper, accent: "#ef4444" },
  "Weather & Earth": { icon: Globe, accent: "#38bdf8" },
  Nature: { icon: Leaf, accent: "#34d399" },
  Music: { icon: MusicNotes, accent: "#a78bfa" },
  Entertainment: { icon: FilmSlate, accent: "#f59e0b" },
  "More Live": { icon: Broadcast, accent: "#9ca3af" },
  "Ambient & Radio": { icon: Radio, accent: "#2dd4bf" },
  Sports: { icon: Trophy, accent: "#22c55e" },
};

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const LivePill = ({ small }) => (
  <span
    className={`inline-flex items-center gap-1.5 bg-crimson font-mono font-bold uppercase tracking-[0.15em] text-white ${
      small ? "px-2 py-0.5 text-[9px]" : "px-2.5 py-1 text-[10px]"
    }`}
  >
    <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" /> Live
  </span>
);

const ChannelCard = ({ v }) => {
  const poster = v.thumbnails?.[1] || v.default_thumb;
  return (
    <Link
      to={v.is_live ? `/watch/${v.video_id}?autoplay=1` : `/watch/${v.video_id}`}
      data-testid={`live-channel-${v.video_id}`}
      tabIndex={0}
      className="rv-row-card group relative w-[280px] shrink-0 snap-start overflow-hidden rounded-lg border border-white/10 bg-[#0A0A0A] transition-all duration-300 hover:border-crimson/60 focus:outline-none focus:ring-2 focus:ring-crimson"
    >
      <div className="relative aspect-video w-full overflow-hidden bg-black">
        <img
          src={poster}
          alt={v.title}
          onError={onImgError}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
        <div className="absolute left-2.5 top-2.5">
          {v.is_live ? (
            <LivePill small />
          ) : (
            <span data-testid={`marathon-badge-${v.video_id}`} className="inline-flex items-center bg-amber px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-[0.15em] text-black">
              Marathon
            </span>
          )}
        </div>
        {v.views > 0 && (
          <div className="absolute right-2.5 top-2.5 flex items-center gap-1 rounded-full bg-black/60 px-2 py-0.5 font-mono text-[9px] text-white/90 backdrop-blur-sm">
            <Eye size={11} weight="fill" className="text-crimson" />
            {formatViews(v.views)}
          </div>
        )}
      </div>
      <div className="p-3">
        <h3 className="line-clamp-2 font-serif text-sm leading-snug text-[#EDEDED] group-hover:text-white">
          {v.title}
        </h3>
        {v.channel_name && (
          <p className="mt-1.5 truncate font-mono text-[10px] uppercase tracking-[0.12em] text-white/40">
            {v.channel_name}
          </p>
        )}
      </div>
    </Link>
  );
};

const CategoryRow = ({ category, streams }) => {
  const meta = CATEGORY_META[category] || CATEGORY_META["More Live"];
  const Icon = meta.icon;
  return (
    <section
      id={`live-section-${slugify(category)}`}
      data-category={category}
      data-testid={`live-category-section-${slugify(category)}`}
      className="scroll-mt-24"
    >
      <div className="mb-4 flex items-center gap-3">
        <span
          className="flex h-9 w-9 items-center justify-center rounded-md"
          style={{ backgroundColor: `${meta.accent}1a`, color: meta.accent }}
        >
          <Icon size={20} weight="duotone" />
        </span>
        <h2 className="font-serif text-2xl tracking-tight text-[#EDEDED]">{category}</h2>
        <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/35">
          {streams.length} channel{streams.length === 1 ? "" : "s"}
        </span>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-4 rv-scroll-x snap-x">
        {streams.map((v) => (
          <ChannelCard key={v.video_id} v={v} />
        ))}
      </div>
    </section>
  );
};

// Live TV networks, matched by tag in this order (first match wins; leftovers go to "More Live").
const NETWORKS = [
  { name: "ReelVault Sports Network", tags: ["sports"], icon: Trophy, accent: "#22c55e" },
  { name: "Action & Blockbuster Marathons", tags: ["action", "thriller", "sci_fi", "sci-fi"], icon: FilmSlate, accent: "#f59e0b" },
  { name: "Retro TV & Animation", tags: ["retro_toons", "adult_animation", "animation"], icon: Television, accent: "#f472b6" },
  { name: "Live News & Weather", tags: ["news", "weather"], icon: Newspaper, accent: "#ef4444" },
  { name: "Ambient & Nature Cams", tags: ["ambient", "nature"], icon: Leaf, accent: "#34d399" },
  { name: "24/7 Radio", tags: ["radio", "music"], icon: Radio, accent: "#a78bfa" },
  { name: "More Live", tags: [], icon: Broadcast, accent: "#9ca3af" },
];
NETWORKS.forEach((n) => { CATEGORY_META[n.name] = { icon: n.icon, accent: n.accent }; });

// Stable per-visit shuffle: order changes on every page load but not on each 60s refetch.
const hash = (str, seed) => {
  let h = seed;
  for (let i = 0; i < str.length; i += 1) h = Math.imul(h ^ str.charCodeAt(i), 2654435761);
  return (h ^ (h >>> 16)) >>> 0;
};

export default function Live() {
  const { data, isLoading } = useQuery({
    queryKey: ["live-streams-networks"],
    queryFn: () => fetchLive(300, undefined, true),
    refetchInterval: 60 * 1000,
  });
  const streams = data || [];
  const [activeCat, setActiveCat] = useState(null);
  const [seed] = useState(() => Math.floor(Math.random() * 1e9));
  const liveCount = streams.filter((v) => v.is_live).length;

  const grouped = useMemo(() => {
    const map = {};
    for (const v of streams) {
      const kws = v.keywords || [];
      const net = NETWORKS.find((n) => n.tags.some((t) => kws.includes(t))) || NETWORKS[NETWORKS.length - 1];
      (map[net.name] = map[net.name] || []).push(v);
    }
    return NETWORKS.filter((n) => map[n.name]?.length).map((n) => ({
      category: n.name,
      streams: [...map[n.name]].sort((a, b) => hash(a.video_id, seed) - hash(b.video_id, seed)),
    }));
  }, [streams, seed]);

  const featured = useMemo(() => {
    const live = streams.filter((v) => v.is_live && /youtube/.test(v.monetized_embed_url || ""));
    const sports = live.filter((v) => (v.keywords || []).includes("sports"));
    return [...(sports.length ? sports : live)].sort((a, b) => (b.views || 0) - (a.views || 0))[0];
  }, [streams]);

  useEffect(() => {
    if (grouped.length && !activeCat) setActiveCat(grouped[0].category);
  }, [grouped, activeCat]);

  // Sync the left-rail highlight as the user scrolls through category sections.
  useEffect(() => {
    if (!grouped.length) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible) {
          const cat = visible.target.getAttribute("data-category");
          if (cat) setActiveCat(cat);
        }
      },
      { rootMargin: "-20% 0px -70% 0px" }
    );
    grouped.forEach(({ category }) => {
      const el = document.getElementById(`live-section-${slugify(category)}`);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [grouped]);

  const scrollTo = (category) => {
    setActiveCat(category);
    document
      .getElementById(`live-section-${slugify(category)}`)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <Helmet>
        <title>Live TV Guide · ReelVault</title>
      </Helmet>
      <Header />

      <OnAirHero stream={featured} liveCount={liveCount} />

      <main className="mx-auto max-w-[1600px] min-[2200px]:max-w-[2200px] px-5 sm:px-8 py-10 rv-fade">
        {isLoading ? (
          <p className="py-20 text-center font-mono text-xs uppercase tracking-[0.2em] text-white/30">Tuning in…</p>
        ) : grouped.length === 0 ? (
          <div data-testid="live-empty" className="flex flex-col items-center py-24 text-center">
            <Television size={56} weight="duotone" className="text-white/25" />
            <h2 className="mt-6 font-serif text-2xl text-[#EDEDED]">No live channels right now</h2>
            <p className="mt-2 max-w-md text-white/45 text-sm">
              Live broadcasts are pulled in automatically. Check back soon, or run the Auto-Sync from the admin panel.
            </p>
          </div>
        ) : (
          <div data-testid="live-epg" className="grid grid-cols-1 gap-8 lg:grid-cols-[220px_1fr] lg:gap-10">
            {/* Left category rail */}
            <aside className="lg:sticky lg:top-32 lg:self-start" data-testid="live-category-nav">
              <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.25em] text-white/30">Guide</p>
              <nav className="flex gap-2 overflow-x-auto pb-2 rv-scroll-x lg:flex-col lg:overflow-visible lg:pb-0">
                {grouped.map(({ category, streams: cs }) => {
                  const meta = CATEGORY_META[category] || CATEGORY_META["More Live"];
                  const Icon = meta.icon;
                  const active = activeCat === category;
                  return (
                    <button
                      key={category}
                      data-testid={`live-cat-nav-${slugify(category)}`}
                      onClick={() => scrollTo(category)}
                      className={`flex shrink-0 items-center gap-3 rounded-md border px-3.5 py-3 text-left transition-all duration-300 lg:w-full ${
                        active
                          ? "border-white/20 bg-white/[0.06]"
                          : "border-transparent bg-transparent hover:border-white/10 hover:bg-white/[0.03]"
                      }`}
                    >
                      <span
                        className="flex h-8 w-8 items-center justify-center rounded"
                        style={{ backgroundColor: `${meta.accent}1a`, color: meta.accent }}
                      >
                        <Icon size={18} weight="duotone" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-serif text-sm text-[#EDEDED]">{category}</span>
                        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/35">
                          {cs.length} live
                        </span>
                      </span>
                    </button>
                  );
                })}
              </nav>
            </aside>

            {/* Category rows */}
            <div className="min-w-0 space-y-12">
              {grouped.map(({ category, streams: cs }) => (
                <CategoryRow key={category} category={category} streams={cs} />
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
